<template>
  <ol class="border border-gray-200 rounded-md divide-y divide-gray-200">
    <li v-for="(term, index) in [...terms].sort(sortTerms)" :key="index" class="pl-3 pr-4 py-3 flex flex-col justify-between sm:flex-row sm:items-center">
      <div class="flex-1 flex">
        <span class="flex-1 ml-2">
          <template v-if="term.Directive">
            <span v-if="index !== 0">Or else, </span>
            <template v-if="term.Directive.mechanism.All">
              <span :class="{ capitalize: index === 0 }">mark</span> the email as <spf-quantifier :qualifier="term.Directive.qualifier" />.
            </template>
            <template v-else-if="term.Directive.mechanism.Include">
              <span class="text-cyan-600 font-semibold" :class="{ capitalize: index === 0 }">include</span> the SPF record at
              <span v-if="term.Directive.mechanism.Include.domain.includes('%')" class="font-semibold">{{ term.Directive.mechanism.Include.domain }}</span>
              <dns-records-link v-else :domain="term.Directive.mechanism.Include.domain" page="SPF" class="text-blue-600 hover:text-blue-800 font-semibold">{{ term.Directive.mechanism.Include.domain }}</dns-records-link> and
              <spf-quantifier :qualifier="term.Directive.qualifier" /> if it matches the sender's IP.
            </template>
            <template v-else-if="term.Directive.mechanism.A">
              <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if the email sender's IP is in the
              <dns-records-link :domain="term.Directive.mechanism.A.domain ? term.Directive.mechanism.A.domain : domain" page="a" class="text-blue-600 hover:text-blue-800 font-semibold">A</dns-records-link>
              <template v-if="term.Directive.mechanism.A.ip4cidr"> (with CIDR <span class="font-semibold">/{{term.Directive.mechanism.A.ip4cidr}}</span>)</template> or
              <dns-records-link :domain="term.Directive.mechanism.A.domain ? term.Directive.mechanism.A.domain : domain" page="aaaa" class="text-blue-600 hover:text-blue-800 font-semibold">AAAA</dns-records-link>
              <template v-if="term.Directive.mechanism.A.ip6cidr"> (with CIDR <span class="font-semibold">/{{term.Directive.mechanism.A.ip6cidr}}</span>)</template> records of
              <span class="font-semibold">{{ term.Directive.mechanism.A.domain ? term.Directive.mechanism.A.domain : domain }}</span>.
            </template>
            <template v-else-if="term.Directive.mechanism.MX">
              <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if the email sender's IP is in the MX records
              <template v-if="term.Directive.mechanism.MX.ip4cidr && term.Directive.mechanism.MX.ip6cidr">(with CIDR <span class="font-semibold">/{{term.Directive.mechanism.MX.ip4cidr}}</span> for IPv4 and <span class="font-semibold">/{{term.Directive.mechanism.MX.ip6cidr}}</span> for IPv6) </template>
              <template v-else-if="term.Directive.mechanism.MX.ip4cidr">(with CIDR <span class="font-semibold">/{{term.Directive.mechanism.MX.ip4cidr}}</span> for IPv4) </template>
              <template v-else-if="term.Directive.mechanism.MX.ip6cidr">(with CIDR <span class="font-semibold">/{{term.Directive.mechanism.MX.ip6cidr}}</span> for IPv6) </template>of
              <span class="font-semibold">{{ term.Directive.mechanism.MX.domain ? term.Directive.mechanism.MX.domain : domain }}</span>.
            </template>
            <template v-else-if="term.Directive.mechanism.PTR">
              <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if a
              <router-link to="/ptr-lookup/" class="text-blue-600 hover:text-blue-800 font-semibold">reverse lookup</router-link> of the email sender's IP resolves to
              <span class="font-semibold">{{ term.Directive.mechanism.PTR.domain ? term.Directive.mechanism.PTR.domain : domain }}</span>.
            </template>
            <template v-else-if="term.Directive.mechanism.IP4">
              <template v-if="term.Directive.mechanism.IP4.ipStart === term.Directive.mechanism.IP4.ipEnd">
                <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if the email sender's IP is
                <span class="font-semibold">{{ term.Directive.mechanism.IP4.ip }}</span>.
              </template>
              <template v-else>
                <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if the email sender's IP is between
                <span class="font-semibold">{{ term.Directive.mechanism.IP4.ipStart }}</span> and <span class="font-semibold">{{ term.Directive.mechanism.IP4.ipEnd }}</span>.
              </template>
            </template>
            <template v-else-if="term.Directive.mechanism.IP6">
              <template v-if="term.Directive.mechanism.IP6.ipStart === term.Directive.mechanism.IP6.ipEnd">
                <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if the email sender's IP is
                <span class="font-semibold">{{ term.Directive.mechanism.IP6.ip }}</span>.
              </template>
              <template v-else>
                <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if the email sender's IP is between
                <span class="font-semibold">{{ term.Directive.mechanism.IP6.ipStart }}</span> and <span class="font-semibold">{{ term.Directive.mechanism.IP6.ipEnd }}</span>.
              </template>
            </template>
            <template v-else-if="term.Directive.mechanism.Exists">
              <spf-quantifier :qualifier="term.Directive.qualifier" :class="{ capitalize: index === 0 }" /> if an A record exists for
              <span class="font-semibold">{{ term.Directive.mechanism.Exists.domain }}</span>.
            </template>
          </template>
          <template v-else-if="term.Exp">
            <span v-if="index !== 0">And </span>
            <span :class="{ capitalize: index === 0 }">query</span> <dns-records-link :domain="term.Modifier.Exp.domain" page="SPF" class="text-blue-600 hover:text-blue-800 font-semibold">{{ term.Modifier.Exp.domain }}</dns-records-link>
            for an explanation when the SPF check fails.
          </template>
          <template v-else-if="term.Redirect">
            <span v-if="index !== 0">Or else, </span>
            <span class="text-cyan-600 font-semibold" :class="{ capitalize: index === 0 }">redirect</span> SPF checking to
            <template v-if="term.Redirect.domain.includes('%')">{{ term.Redirect.domain }}</template>
            <dns-records-link v-else :domain="term.Redirect.domain" page="SPF" class="text-blue-600 hover:text-blue-800 font-semibold">{{ term.Redirect.domain }}</dns-records-link>.
          </template>
          <template v-else-if="term.UnknownModifier">
            <span v-if="index !== 0">And </span>
            <span :class="{ capitalize: index === 0 }">ignore</span> this unknown modifier.
          </template>
        </span>
      </div>
      <div class="ml-2 flex-shrink-0 text-gray-500 text-left sm:ml-4">
        <template v-if="term.Directive">
          <template v-if="term.Directive.mechanism.All">
            {{ encodeQualifier(term.Directive.qualifier) }}all
          </template>
          <template v-else-if="term.Directive.mechanism.Include">
            {{ encodeQualifier(term.Directive.qualifier) }}include:{{ term.Directive.mechanism.Include.domain }}
          </template>
          <template v-else-if="term.Directive.mechanism.A">
            {{ encodeQualifier(term.Directive.qualifier) }}a{{ term.Directive.mechanism.A.domain ? ':' + term.Directive.mechanism.A.domain : '' }}{{ term.Directive.mechanism.A.ip4cidr ? `/${term.Directive.mechanism.A.ip4cidr}` : '' }}{{ term.Directive.mechanism.A.ip6cidr ? `//${term.Directive.mechanism.A.ip6cidr}` : '' }}
          </template>
          <template v-else-if="term.Directive.mechanism.MX">
            {{ encodeQualifier(term.Directive.qualifier) }}mx{{ term.Directive.mechanism.MX.domain ? ':' + term.Directive.mechanism.MX.domain : '' }}{{ term.Directive.mechanism.MX.ip4cidr ? `/${term.Directive.mechanism.MX.ip4cidr}` : '' }}{{ term.Directive.mechanism.MX.ip6cidr ? `//${term.Directive.mechanism.MX.ip6cidr}` : '' }}
          </template>
          <template v-else-if="term.Directive.mechanism.PTR">
            {{ encodeQualifier(term.Directive.qualifier) }}ptr{{ term.Directive.mechanism.PTR.domain ? ':' + term.Directive.mechanism.PTR.domain : '' }}
          </template>
          <template v-else-if="term.Directive.mechanism.IP4">
            {{ encodeQualifier(term.Directive.qualifier) }}ip4:{{ term.Directive.mechanism.IP4.ipStart }}{{ term.Directive.mechanism.IP4.cidr ? `/${term.Directive.mechanism.IP4.cidr}` : '' }}
          </template>
          <template v-else-if="term.Directive.mechanism.IP6">
            {{ encodeQualifier(term.Directive.qualifier) }}ip6:{{ term.Directive.mechanism.IP6.ipStart }}{{ term.Directive.mechanism.IP6.cidr ? `/${term.Directive.mechanism.IP6.cidr}` : '' }}
          </template>
          <template v-else-if="term.Directive.mechanism.Exists">
            {{ encodeQualifier(term.Directive.qualifier) }}exists:{{ term.Directive.mechanism.Exists.domain }}
          </template>
        </template>
        <template v-if="term.Exp">
          exp={{ term.Exp.domain }}
        </template>
        <template v-else-if="term.Redirect">
          redirect={{ term.Redirect.domain }}
        </template>
        <template v-else-if="term.UnknownModifier">
          {{ term.UnknownModifier.name }}={{ term.UnknownModifier.macroString }}
        </template>
      </div>
    </li>
  </ol>
</template>

<script setup lang="ts">
import { PropType } from 'vue';
import SpfQuantifier from '@/components/records/SpfQuantifier.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import { Qualifier, Term } from '@/model/Spf';

defineProps({
  terms: {
    type: Object as PropType<Array<Term>>,
    required: true,
  },
  domain: {
    type: String,
    required: true,
  },
});

function encodeQualifier(qualifier: Qualifier) {
  switch (qualifier) {
    case 'Pass': return '+';
    case 'Fail': return '-';
    case 'Softfail': return '~';
    case 'Neutral': return '?';
    case 'None': return '';
    default: return '';
  }
}

function sortTerms(l: Term, r: Term) {
  function toValue(t: Term) {
    if ('Directive' in t) return 1;
    if ('Redirect' in t) return 2;
    if ('Exp' in t) return 3;
    if ('UnknownModifier' in t) return 4;
    return 0;
  }
  return toValue(l) - toValue(r);
}
</script>
